import * as d3 from 'd3-selection'
import results from './results.js'
import position from './position.js'
import suggestion from './suggestion.js'
import * as interactions from '../interactions.js'
import scrollIntoView from './scroll-into-view.js'

export default function (selection) {
  selection.each(function (data) {
    var select = d3.select(this)
      , observer = null
      , menu = d3.select(data.attachToWrapper ? this : document.body)
                 .selectAll('.select-menu#' + data.id)
                 .data(function () {
                   if (data.disabled) {
                     return []
                   }
                   if (!data.open) {
                     return []
                   }

                   if (data.render.resultset) {
                     // There is a resultset renderer, which means someone wants full control of the menu
                     // Must continue
                     return [data]
                   }

                   if (data.menuOptions.length === 0 && !(data.filter && data.filterSuggestion)) {
                     return []
                   }
                   return [data]
                 })
      , enter = menu.enter()
                    .append('div')
                      .attr('id', function (d) { return d.id })
      , _menu = enter.merge(menu)

    enter.append('div')
         .attr('class', 'select-menu-contents')

    enter.select(function () {
            /* When data emits change:highlighted (e.g. from input down with up/down arrows),
             * we need to update the list. Rather than drawing all components, we listen from
             * within here (the menu), and just update those li elements to keep things fast.
             */
            data.on('change:highlighted', function (d) {
              // Remove whatever was highlighted
              _menu.selectAll('li.highlighted')
                   .classed('highlighted', false)
                   .each(d => d.highlighted = false)

              if (d) {
                  // Highlight the new option
                _menu.selectAll('li')
                     .classed('highlighted', d => d.highlighted)

                // Now scroll the new highlighted option into view
                _menu.selectAll('li.highlighted')
                     .call(scrollIntoView, _menu.select('.select-menu-results').node())
              }
            })
          })

    enter.filter(d => !d.attachToWrapper) // Find floating menus
         .select(function (d) {
           // When the menu is open, setup a new observer on all ancestors to see if they are removed from the DOM
           observer = new MutationObserver(function (mutations) {
             mutations.forEach(function (mutation) {
               if (mutation.removedNodes.length) {
                 var removed = [].filter.call(mutation.removedNodes, function (removed) {
                   return removed.nodeType === Node.ELEMENT_NODE && // must check to make sure removed is an element, bc IE11 will blow up
                          (removed.contains(select.node()) || removed == select.node())
                 })

                 if (removed.length) {
                   interactions.close(data)
                 }
               }
             })
           })

           ;(d._ancestors || []).forEach(function (node) {
             observer.observe(node, {
               childList: true
             })
           })

           // Now set an initial position... here's why:
           // The menu is memory at this point, but when we call results, it might set the menu scrollTop, which will
           // trigger a reflow. In slow browsers (i.e. IE11), the menu will flash at top: 0, left: 0 since its position isn't set
           // We can't set its true position because we don't know if it will be positioned above or below the control, so let's default directly below
           d3.select(this)
             .call(position, select.select('.select-control').node().getBoundingClientRect())
         })

    menu.exit()
        .remove()
        .select(function () {
          data.removeAllListeners('change:highlighted')
          select.classed('menu-above', false)
                .classed('menu-below', false)
          if (observer) {
            observer.disconnect()
            observer = null
          }
        })

    _menu.attr('class', 'select-menu ' + [].map.call(this.classList, c => `-select-${c}`).join(' '))
         .selectAll('.select-menu-contents')
         .call(results)
         .selectAll('.select-menu-results')
         .call(suggestion)

    _menu.filter(d => !d.attachToWrapper)
         .select(function () {
           // Now that the menu has substance, truly position it.
           // This cannot be positioned on just .enter() in case changing the value of the select causes the
           // select's control position on the page to change.
           var control = select.select('.select-control').node()
             , rect = control.getBoundingClientRect()
             , results = d3.select(this).selectAll('.select-menu-results').node()
             , rtl = document.documentElement.getAttribute('dir') == 'rtl'
             , menu = d3.select(this)
                        .call(position, rect, rtl)
                        // can't use `offsetWidth` b/c it rounds. can't use `window.getComputedStyle` b/c ie11
                        // doesn't seem to respect box-sizing when answering the width
                        .style('min-width', `${rect.width}px`)
                        // Add the .has-scrollable-content class when the menu has vertical
                        // scrollable content. This is used to detect when a scrollbar is visible
                        // and we'll need to compensate for it width-wise.
                        // https://github.com/SpiderStrategies/Scoreboard/issues/30135
                        .classed('has-scrollable-content', () => {
                          return results.scrollHeight > results.clientHeight
                        })

           menu.filter(() => rtl)
               .style('right', `${window.innerWidth - rect.right}px`)

           menu.filter(() => {
                  return !d3.select(this).classed('menu-right-align')
                })
                .style('max-width', () => {
                  // When the menu flows longer than the control (i.e. not right-aligned),
                  // cap the menu so it doesn't extend beyond the browser window. See SB #26089
                  return rtl ? `${rect.width + Math.abs(rect.left)}px` : `${window.innerWidth - rect.left}px`
                })

           select.classed('menu-above', menu.classed('menu-above'))
                 .classed('menu-below', menu.classed('menu-below'))
         })
  })
}
